/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components */
// Extends From
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember Imports
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
// External Imports
import QrStatus from '@linc-technologies/linced-common/enums/qr-status';
import { classNames } from '@ember-decorators/component';

/**
 * onRegenerateQr provides a callback that receives a person id and the index of
 * where in the QrCode array the record is stored when the "regenerate QR Code"
 * button is clicked.
 *
 * @name onRegenerateQr
 * @function
 * @param {number} index - The position of the QR Code array to inject the new record.
 * @param {String} personId - The unique person to regenerate a QR Code for.
 */

@classNames('qr-code-display')
export default class QrCodeDisplay extends Component {
  QrStatus = QrStatus;

  // Params
  /**
   * onRegenerateQr provides a callback that receives a person id and the index of
   * where in the QrCode array the record is stored when the "regenerate QR Code"
   * button is clicked.
   *
   * @param {onRegenerateQr}
   */
  onRegenerateQr = () => {};

  /**
   * qrCodes provides an array of ember auth/qr-code models.
   * @param {DS.AdapterPopulatedRecordArray<'module:linced-app/models/auth/qr-code'>}
   */
  @tracked qrCodes = null;

  /**
   * recordIsLoading provides a boolean to specify if a record is loading.
   * @param {Boolean}
   */
  @tracked recordIsLoading = false;

  /**
   * shouldScroll provides a way to disable auto scrolling to the selected
   * element.
   * @param {Boolean}
   */
  shouldScroll = true;

  // State
  /**
   * position stores the current index of the qr code to be displayed.
   */
  @tracked position = 0;

  // Methods
  /**
   * currentQrCode returns the QR code model at the set position.
   *
   * @return {(DS.Model<'module:linced-app/models/auth/qr-code'>|null)}
   */
  get currentQrCode() {
    if (isArray(this.qrCodes)) {
      return this.qrCodes.at(this.position);
    }

    return null;
  }

  /**
   * currentQrCodeErrorMessage returns a human understandable error to guide
   * users in how to fix the problem.
   *
   * @return {Promise<String>}
   */
  @computed('currentQrCode.{status,person.fullName}')
  get currentQrCodeErrorMessage() {
    let personFullName = this.get('currentQrCode.person.fullName');
    let personName = '';
    let nameOrPronoun = 'their';
    if (!isEmpty(personFullName)) {
      personName = ` for ${personFullName}`;
      nameOrPronoun = `${personFullName}'s`;
    }

    let qrCode = this.currentQrCode;
    let errMsg = 'An error has occurred, please try again';
    switch (qrCode.status) {
      case QrStatus.OK:
        return '';

      case QrStatus.ERROR:
        errMsg = `An error occurred while attempting to generate a sign in QR Code${personName}, please try again`;
        break;

      case QrStatus.USER_NOT_FOUND:
        errMsg = `A user account was not found${personName}. Please ask your School's Hero Administrator to ensure they have been assigned an email address.`;
        break;

      case QrStatus.USER_ACCOUNT_DISABLED:
        errMsg = `The user account${personName} is disabled. Please ask your School's Hero Administrator to enable ${nameOrPronoun} access to Hero.`;
        break;
    }

    return errMsg;
  }

  /**
   * shouldShowNavigation removes rendering of navigation buttons if only one
   * QR Code is present.
   *
   * @return {Boolean}
   */
  get shouldShowNavigation() {
    return isArray(this.qrCodes) && this.qrCodes.length > 1;
  }

  /**
   * shouldNavigateLeft computes whether the left carousel navigation button
   * should be enabled.
   *
   * @return {Boolean}
   */
  get shouldNavigateLeft() {
    return !this.recordIsLoading && this.position > 0;
  }

  /**
   * shouldNavigateRight computes whether the right carousel navigation button
   * should be enabled.
   *
   * @return {Boolean}
   */
  get shouldNavigateRight() {
    return !this.recordIsLoading && this.position < this.qrCodes.length - 1;
  }

  /**
   * navigates to the previous QR Code, if position greater than 0.
   * On hitting the start, will not rotate to the end of the array.
   */
  @action
  goToPrevious() {
    const pos = this.position;
    if (pos > 0) {
      this.position = pos - 1;
    }
  }

  /**
   * goTo navigates to the QR Code at the specified index, if it falls within
   * expected bounds.
   *
   * @param {number} index - The QR Code index to navigate to.
   */
  @action
  goTo(index) {
    if (index >= 0 && index <= this.qrCodes.length - 1) {
      this.position = index;
    }
  }

  /**
   * goToNext navigates to the next QR Code, if position less than the number
   * of QR Codes.
   * On hitting the end, will not rotate to the start of the array.
   */
  @action
  goToNext() {
    const pos = this.position;
    if (pos < this.qrCodes.length - 1) {
      this.position = pos + 1;
    }
  }
}
